import React = require('react');
import { Formik, FormikProps } from 'formik';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { UserSettings } from '../services/resources/UserSettings';
import _ = require('underscore');

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';

import { Messages } from '../../codegen/Localize';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { NotificationsSettingsProps } from './NotificationsSettings';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

const DEEPLINK_ID = 'data-alert-notification';

export interface DataAlertNotificationField {
    dataAlertNotificationEnabled: boolean;
}

function showButtons(formik: FormikProps<DataAlertNotificationField>, dataAlertNotificationEnabled: boolean) {
    if (!formik.touched || !formik.isValid || formik.isSubmitting || _.isEqual(formik.values.dataAlertNotificationEnabled, dataAlertNotificationEnabled)) {
        return false;
    }
    return true;
}

export function DataAlertNotification(props: NotificationsSettingsProps): JSX.Element {
    const [currentDataAlertNotificationEnabled, setCurrentDataAlertNotificationEnabled] = React.useState(false);

    async function handleSubmit(values: DataAlertNotificationField) {
        UserSettings.updateSuspendedDataAlertSetting(props.siteUser.id, values.dataAlertNotificationEnabled).then(() => {
            setCurrentDataAlertNotificationEnabled(values.dataAlertNotificationEnabled);
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });
        }).catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() });
        });
    }

    React.useEffect(
      () => {
        UserSettings.getSuspendedDataAlertSetting(props.serverUser.id)
          .then((result: boolean) => {
            setCurrentDataAlertNotificationEnabled(result);
          })
          .catch(() => {});
      },
      // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    return (
        <Formik
            initialValues={{ dataAlertNotificationEnabled: currentDataAlertNotificationEnabled }}
            onSubmit={(values: DataAlertNotificationField) => handleSubmit(values)}
            enableReinitialize={true}
        >
            {
                (formik: FormikProps<DataAlertNotificationField>) => (
                    <DeepLink deepLinkId={DEEPLINK_ID}>
                        <form onSubmit={formik.handleSubmit}>
                            <table>
                                <tbody className={UserSettingsStyle.tbody}>
                                    <tr data-tb-test-id="userSettings-data-alert-notification">
                                        <td className={UserSettingsStyle.labelText}>
                                            {Messages.dataAlertsTitle()}
                                    </td>
                                        <td className={UserSettingsStyle.detailsValue}>
                                            {Messages.dataAlertSuspendedNotificationUserSettingDetail()}
                                    </td>
                                    </tr>
                                    <tr>
                                        <td className={UserSettingsStyle.labelText} />
                                        <td className={UserSettingsStyle.detailsValue}>
                                            <FormikCheckbox
                                                containerStyle={{ marginLeft: 0 }}
                                                testId='Data-Alert-Notification-Enabled'
                                                name={'dataAlertNotificationEnabled'}
                                                disabled={props.userHasNoSiteId}
                                                label={Messages.dataAlertSuspendedNotificationUserSettingEnable()} />
                                        </td>
                                    </tr>
                                    {
                                        showButtons(formik, currentDataAlertNotificationEnabled) &&
                                        <InlineCancelAndSaveButtons
                                            testId={'data-alert-notification'}
                                            formik={formik}
                                        />
                                    }
                                </tbody>
                            </table>
                        </form>
                    </DeepLink>
                )
            }
        </Formik>
    );
}

export const DataAlertSettingBeaconConfig = beaconComponentRegistryEntry({
    command: () =>  Messages.dataAlertsTitle(),
    deeplinkId: DEEPLINK_ID,
    url: (props) => getUserAccountSettingsURL(props.sessionInfo),
    tags:  () => { return [Messages.dataAlertSuspendedNotificationUserSettingDetail(), Messages.userNotificationSettingsLabel()];},
    isAdmin: true // props.isViewerAdmin
  });